// MerchantLedger.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";
import StreakLedgerItem from "./StreakLedgerItem";

// const MerchantLedger = () => {
const MerchantLedger = ({ streakModules = ["FindItLocal"], title = "📒 Merchant Ledger" }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [customerFilter, setCustomerFilter] = useState("ALL");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    const loadLedger = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetchData({
                url: "/pd/streaks/ledger-merchant",
                method: "post",
                // data: { shopkeeperCode: user?.think5whyscode || "" },
                data: { shopkeeperCode: user?.think5whyscode || "", streakModules },
                headers: { Authorization: token },
            });
            if (res?.success) setRecords(res.data || []);
            else setError(res?.message || "Failed to load ledger");
        } catch (err) {
            setError(err?.message || "Error loading ledger");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadLedger(); }, []);

    const customerOptions = useMemo(() => {
        const map = {};
        records.forEach((r) => {
            if (r.userCode) map[r.userCode] = { userCode: r.userCode, userName: r.userName };
        });
        return Object.values(map);
    }, [records]);

    const filtered = useMemo(() => {
        const searchLower = search.trim().toLowerCase();
        return records.filter((r) => {
            const customerMatch = customerFilter === "ALL" || r.userCode === customerFilter;
            const searchMatch = !searchLower ||
                r.userName?.toLowerCase().includes(searchLower) ||
                r.userCode?.toLowerCase().includes(searchLower);
            return customerMatch && searchMatch;
        });
    }, [records, customerFilter, search]);

    const totals = useMemo(() => ({
        in:  filtered.filter(r => r.inOutType === "In").reduce((s, r) => s + Number(r.points || 0), 0),
        out: filtered.filter(r => r.inOutType === "Out").reduce((s, r) => s + Number(r.points || 0), 0),
    }), [filtered]);

    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
            <div className="flex items-center justify-between px-1">
                <div className="text-cyan-300 font-bold text-sm">📒 Merchant Ledger</div>
                <div className="text-cyan-300 font-bold text-sm">📒 {title}</div>
                <button
                    onClick={loadLedger}
                    disabled={loading}
                    className="text-[10px] w-fit px-2 py-1 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 hover:border-cyan-500/50"
                >
                    {loading ? "Refreshing..." : "🔄 Refresh"}
                </button>
            </div>

            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm">🔍</span>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search customer name or code…"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                />
            </div>

            {customerOptions.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setCustomerFilter("ALL")}
                        className={`w-fit px-2 py-1 text-[10px] rounded-md border ${
                            customerFilter === "ALL" ? "bg-cyan-500 border-cyan-500 text-black font-bold" : "bg-gray-700 border-gray-600 text-gray-100"
                        }`}
                    >
                        All Customers
                    </button>
                    {customerOptions.map((c) => (
                        <button
                            key={c.userCode}
                            onClick={() => setCustomerFilter(c.userCode)}
                            className={`w-fit px-2 py-1 text-[10px] rounded-md border ${
                                customerFilter === c.userCode ? "bg-cyan-500 border-cyan-500 text-black font-bold" : "bg-gray-700 border-gray-600 text-gray-100"
                            }`}
                        >
                            {c.userName || c.userCode}
                        </button>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                    <div className="font-black text-base text-green-400">+{totals.in}</div>
                    <div className="text-[10px] text-gray-100">Points In</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                    <div className="font-black text-base text-red-400">-{totals.out}</div>
                    <div className="text-[10px] text-gray-100">Points Redeemed</div>
                </div>
            </div>

            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-6 text-center text-gray-400 text-xs">Loading...</div>
                ) : error ? (
                    <div className="p-4 text-center text-red-300 text-xs">⚠️ {error}</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className="text-3xl mb-2">📒</div>
                        <div className="text-gray-200 text-sm">No ledger entries yet</div>
                    </div>
                ) : (
                    <AnimatePresence>
                        {filtered.map((item) => (
                            <motion.div key={item._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                <StreakLedgerItem item={item} selected={selected} setSelected={setSelected} />
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
};

export default MerchantLedger;